/** The launch-policy picker of the go-live surfaces: the Run board's tenant-default setter and
 *  the easy flow's Go live. One select over the tenant's launch-state policies (stage order,
 *  Draft → Soft launched → Hard launched), so both offer the same list in the same words. */
import { Box, MenuItem, TextField, type SxProps, type Theme } from '@mui/material';

import type { LaunchPolicyRef } from '@/api/preflight';

/** What a policy's flags admit, in the words the runbook uses. */
export function policyAdmits(flags: Record<string, boolean>): string {
  const parts = [flags.incoming_webhooks ? 'bookings in' : 'bookings dropped'];
  if (flags.chat_enabled) parts.push('chat');
  const guest =
    flags.outgoing_emails || flags.outgoing_sms || flags.outgoing_whatsapp || flags.outgoing_airbnb;
  const explicitOnly =
    flags.outgoing_emails_to_address ||
    flags.outgoing_sms_to_number ||
    flags.outgoing_whatsapp_to_number;
  if (guest) parts.push('guest channels open');
  else if (explicitOnly) parts.push('messages to explicit addresses/numbers only');
  else parts.push('nothing out');
  return parts.join(' · ');
}

/** Does the policy admit incoming bookings (`incoming_webhooks`) — the go-live requirement? */
export function admitsBookings(p: Pick<LaunchPolicyRef, 'flags'>): boolean {
  return p.flags.incoming_webhooks === true;
}

interface Props<P extends LaunchPolicyRef> {
  policies: readonly P[];
  value: number | '';
  onChange: (id: number | '') => void;
  label: string;
  ariaLabel: string;
  /** Each option also says what the policy admits (a muted line under its name). */
  describe?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

export function LaunchPolicyPicker<P extends LaunchPolicyRef>({
  policies,
  value,
  onChange,
  label,
  ariaLabel,
  describe = false,
  disabled,
  fullWidth,
  sx,
}: Props<P>) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      disabled={disabled}
      fullWidth={fullWidth}
      onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
      sx={sx}
      inputProps={{ 'aria-label': ariaLabel }}
      // The closed field shows the name only; the open list can say what each admits.
      SelectProps={
        describe
          ? { renderValue: (id) => policies.find((p) => p.id === id)?.name ?? '' }
          : undefined
      }
    >
      {policies.map((p) => (
        <MenuItem key={p.id} value={p.id}>
          {describe ? (
            <Box sx={{ display: 'grid' }}>
              <span>{p.name}</span>
              <Box component="span" sx={{ typography: 'caption', color: 'text.secondary' }}>
                {policyAdmits(p.flags)}
              </Box>
            </Box>
          ) : (
            p.name
          )}
        </MenuItem>
      ))}
    </TextField>
  );
}
